import { WeatherLocation } from "./types";

type ReverseGeocodingResponse = {
  city?: string;
  locality?: string;
  principalSubdivision?: string;
  countryName?: string;
  countryCode?: string;
};

function pickName(data?: ReverseGeocodingResponse) {
  return data?.city || data?.locality || data?.principalSubdivision || "Current location";
}

function pickAdmin1(data?: ReverseGeocodingResponse) {
  return data?.principalSubdivision;
}

function pickCountry(data?: ReverseGeocodingResponse) {
  return data?.countryName || "Current location";
}

function pickCountryCode(data?: ReverseGeocodingResponse) {
  return data?.countryCode?.toUpperCase();
}

async function reverseGeocode(latitude: number, longitude: number) {
  const response = await fetch(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
  );
  if (!response.ok) {
    throw new Error(`Reverse geocoding failed: ${response.status}`);
  }
  return (await response.json()) as ReverseGeocodingResponse;
}

export async function resolveCurrentLocation(): Promise<WeatherLocation> {
  const position = await new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 15 * 60 * 1000,
    });
  });

  const latitude = Number(position.coords.latitude.toFixed(4));
  const longitude = Number(position.coords.longitude.toFixed(4));

  let geo: ReverseGeocodingResponse | undefined;
  try {
    geo = await reverseGeocode(latitude, longitude);
  } catch {
    geo = undefined;
  }

  return {
    id: `current:${latitude},${longitude}`,
    name: pickName(geo),
    admin1: pickAdmin1(geo),
    country: pickCountry(geo),
    countryCode: pickCountryCode(geo),
    latitude,
    longitude,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
